/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/** Defines the water-loss KPI cards displayed above the water anomaly chart. */

/* Import configuration */
import {
    WATER_LOSS_RATE_CRITICAL_LITRES_PER_HOUR,
    WATER_LOSS_RATE_WARNING_LITRES_PER_HOUR,
} from '@/config/app';

/* Import libraries */
import { roundToSignificantDigits } from '@/lib/formatting/significantDigits';
import { getCompletedMonthPeriod } from '@/lib/timeSeries/calendarLoss';
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import type { TimeSeriesTrafficLightConfig } from '@/lib/timeSeries/timeSeriesValue';

type Translate = (key: string, values?: Record<string, number | string>) => string;

const HOURS_PER_DAY = 24;
const HOURS_PER_SEVEN_DAYS = HOURS_PER_DAY * 7;

interface WaterLossDetailKpiConfigOptions {
    deviceSensor: DeviceSensor;
    t: Translate;
}

function createCumulativeWaterLossLegendCode(
    cumulativeLossSensorName: string,
    lookback?: { amount: number; toleranceDays: number; unit: 'month' | 'year' },
): string {
    const period = lookback ? getCompletedMonthPeriod(lookback.unit === 'year' ? 12 : 1) : null;
    const comparison = period && lookback?.unit === 'year'
        ? `// Erste und letzte gültige Messung innerhalb der letzten zwölf abgeschlossenen Monate.\n` +
            `const readings = validReadingsInPeriod('${period.from}', '${period.to}'); // Ende exklusiv\n` +
            `const lossApiValue = last(readings) - first(readings);`
        : period
        ? `// Abgeschlossene Kalendermonate; Ende exklusiv.\n` +
            `// Monatsgrenzen: nächster echter Messwert innerhalb +/- 14 Tagen, bei Gleichstand der frühere.\n` +
            `// Keine eigenen Messwerte im Zeitraum oder Zählerrücksetzung: kein Wert.\n` +
            `const lossApiValue = nearestValueWithin14Days('${period.to}') - nearestValueWithin14Days('${period.from}');`
        : `const lossApiValue = currentApiValue;`;

    return `// Zählernummer: ${cumulativeLossSensorName}
// Zeitbereich der Zeitreihe: NOW-25MONTHS bis NOW
const currentApiValue = last(timeSeriesValues['${cumulativeLossSensorName}']);
${comparison}
const waterLossCubicMetres = lossApiValue;`;
}

function createWaterLossRateKpi(
    waterLossRateSensorName: string,
    dtcSensorName: string,
    title: string,
    conversion: number,
    unit: string,
) {
    return {
        aggregation: 'last' as const,
        display: {
            conversion,
            series: waterLossRateSensorName,
            unit,
        },
        from: 'NOW-4WEEKS',
        series: dtcSensorName,
        showDate: true,
        showLegend: true,
        title,
        to: 'NOW',
        trafficLight: {
            rules: [
                { severity: 'danger' as const, value: 200, when: 'gte' as const },
                { severity: 'warning' as const, value: 100, when: 'gte' as const },
            ],
            fallback: 'success' as const,
        },
        urn: [waterLossRateSensorName, dtcSensorName],
        transformValue: (value: number) => roundToSignificantDigits(value, 2),
    };
}

function createCumulativeWaterLossCarouselItem({
    key,
    lookback,
    noDataMessage,
    rateSensorName,
    title,
    cumulativeLossSensorName,
}: {
    cumulativeLossSensorName: string;
    key: string;
    lookback?: { amount: number; toleranceDays: number; unit: 'month' | 'year' };
    noDataMessage?: string;
    rateSensorName: string;
    title: string;
}) {
    const trafficLight: TimeSeriesTrafficLightConfig = {
        rules: [
            {
                severity: 'danger',
                value: WATER_LOSS_RATE_CRITICAL_LITRES_PER_HOUR / 1000,
                when: 'gte',
            },
            {
                severity: 'warning',
                value: WATER_LOSS_RATE_WARNING_LITRES_PER_HOUR / 1000,
                when: 'gte',
            },
        ],
        fallback: 'success',
    };

    return {
        aggregation: 'last' as const,
        conversion: 1,
        key,
        legendCode: createCumulativeWaterLossLegendCode(cumulativeLossSensorName, lookback),
        legendConversion: 1000,
        legendFractionDigits: 0,
        legendUnit: 'l/h',
        calendarPeriod: lookback ? getCompletedMonthPeriod(lookback.unit === 'year' ? 12 : 1) : undefined,
        useAvailablePeriodValues: lookback?.unit === 'year',
        noDataMessage,
        resolveTrafficLightValue: ({ values }: { values: Record<string, number | null> }) => (
            values[rateSensorName] ?? null
        ),
        resolveUnit: () => 'm³',
        title,
        transformValue: (cubicMetres: number) => roundToSignificantDigits(cubicMetres, 2),
        trafficLight,
        trimTrailingZeros: true,
        urn: [cumulativeLossSensorName, rateSensorName],
    };
}

/** Creates the rate cards and the cumulative-loss carousel displayed above the water chart. */
export function createWaterLossDetailKpiConfigs({
    deviceSensor,
    t,
}: WaterLossDetailKpiConfigOptions) {
    const dtcLast7DaysSensorName = deviceSensor.getSensorName('WL', 'dtc_7d');
    const dtcSensorName = deviceSensor.getSensorName('WL', 'dtc');
    const cumulativeLossSensorName = deviceSensor.getSensorName('WL', 'V_loss');
    const waterLossLastDayRateSensorName = deviceSensor.getSensorName('WL', 'qv_loss_1d');
    const waterLossRateSensorName = deviceSensor.getSensorName('WL', 'qv_loss_7d');

    if (
        dtcLast7DaysSensorName === null ||
        dtcSensorName === null ||
        cumulativeLossSensorName === null ||
        waterLossLastDayRateSensorName === null ||
        waterLossRateSensorName === null
    ) {
        return null;
    }

    return {
        waterLossLast7Days: createWaterLossRateKpi(
            waterLossRateSensorName,
            dtcLast7DaysSensorName,
            t('Average water loss last 7 days'),
            HOURS_PER_SEVEN_DAYS,
            'm³ / 7 Tage',
        ),
        waterLossLastDay: createWaterLossRateKpi(
            waterLossLastDayRateSensorName,
            dtcSensorName,
            t('Average water loss last day'),
            HOURS_PER_DAY,
            'm³ / Tag',
        ),
        cumulativeLoss: {
            autoAdvanceMs: 8000,
            from: 'NOW-25MONTHS',
            showDate: true,
            showLegend: true,
            to: 'NOW',
            items: [
                createCumulativeWaterLossCarouselItem({
                    cumulativeLossSensorName,
                    key: 'water_loss_overall',
                    rateSensorName: waterLossRateSensorName,
                    title: t('Water loss overall'),
                }),
                createCumulativeWaterLossCarouselItem({
                    cumulativeLossSensorName,
                    key: 'water_loss_last_year',
                    lookback: { amount: 1, toleranceDays: 31, unit: 'year' },
                    noDataMessage: t('No historical comparison value is available.'),
                    rateSensorName: waterLossRateSensorName,
                    title: t('Water loss last 12 complete months'),
                }),
                createCumulativeWaterLossCarouselItem({
                    cumulativeLossSensorName,
                    key: 'water_loss_last_month',
                    lookback: { amount: 1, toleranceDays: 7, unit: 'month' },
                    noDataMessage: t('No historical comparison value is available.'),
                    rateSensorName: waterLossRateSensorName,
                    title: t('Water loss previous month'),
                }),
            ],
        },
    };
}
